<template>
  <div>
    <div
      ref="modalWrapper"
      class="ds-modal-wrapper"
      :class="[{
        'ds-modal-wrapper--visible': isOpen,
        'ds-modal-wrapper--mobile-bottom': isMobile && isMobileBottom && !mobileFullScreenModal,
        'ds-modal-wrapper--noAnimation': noAnimation,
        'ds-modal-wrapper--mobileFullScreenModal': isMobile && mobileFullScreenModal,
        'ds-modal-wrapper--topAligned': topAligned && !(isMobile && mobileFullScreenModal),
        'ds-modal-wrapper--sidebar': size === 'sidebar',
      }, wrapperClassName]"
      @click="closeOnBackgroundClick"
    >
      <div
        ref="modal"
        class="ds-modal"
        :class="[sizeClass, className]"
        @click.stop="stoppedPropagation"
        @mousedown="modalMouseDown"
        @mouseup="modalMouseUp"
      >
        <div class="ds-modal-header">
          <div
            v-if="$slots.title"
            class="ds-modal-title"
          >
            <slot name="title" />
          </div>
          <div
            v-else-if="title"
            class="ds-modal-title"
          >
            {{ title }}
          </div>
          <div v-else />

          <IconButton
            v-if="showCloseButton"
            type="button"
            class="ds-modal__close_button"
            aria-label="Close"
            tabindex="1"
            @click="$emit('close')"
            @keydown.space="$emit('close')"
          >
            <TagIcon
              :icon="CloseIcon"
              color="mono-black"
              class="h-5 w-5"
            />
          </IconButton>
        </div>

        <div class="ds-modal-content">
          <slot />
        </div>
        <div
          v-if="$slots.actions"
          class="ds-modal-actions"
        >
          <slot name="actions" />
        </div>
      </div>
    </div>
    <div
      ref="modalOverlay"
      class="ds-modal-overlay"
      :class="{
        'ds-modal-overlay--visible': isOpen,
        'ds-modal-overlay--noAnimation': noAnimation,
        'ds-modal-overlay--blurBackground': blurBackground,
      }"
    />
  </div>
</template>

<script>
import CloseIcon from '@tagvenue/icons/24/bold/general/x-close.svg';
import throttle from 'lodash/throttle';
import { markRaw } from 'vue';

import TagIcon from '@/components/TagIcon.vue';

import ScreenWidth from '../../ScreenWidth';
import IconButton from '../Buttons/IconButton.vue';

export default {
  name: 'BaseModal',
  mixins: [ScreenWidth],
  components: { TagIcon, IconButton },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true,
    },
    closeOnEscKeyClick: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    size: {
      validator(value) {
        return ['small', 'large', 'full', 'fullscreen', 'default', 'sidebar'].indexOf(value) !== -1;
      },
      default: 'default',
    },
    wrapperClassName: {
      type: String,
      default: null,
    },
    className: {
      type: String,
      default: null,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    isMobileBottom: {
      type: Boolean,
      default: true,
    },
    mobileFullScreenModal: {
      type: Boolean,
      default: false,
    },
    noAnimation: {
      type: Boolean,
      default: false,
    },
    blurBackground: {
      type: Boolean,
      default: false,
    },
    stopScrollClass: {
      type: String,
      default: 'disabled-scroll',
    },
    forceTopAligned: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      CloseIcon: markRaw(CloseIcon),
      scrollTop: 0,
      hasLockedScroll: false,
      scrollbarPresent: false,
      isMousePressedWithinModal: false,
      topAligned: false,
      resizeModalObserver: null,
    };
  },
  computed: {
    sizeClass() {
      return {
        default: '',
        small: 'ds-modal--small',
        large: 'ds-modal--large',
        full: 'ds-modal--full',
        fullscreen: 'ds-modal--fullscreen',
        sidebar: 'ds-modal--sidebar',
      }[this.size];
    },
  },
  watch: {
    isOpen(isOpen) {
      this.updateScroll();

      if (isOpen) {
        this.setZIndex();
        this.addHeightObserver();
      } else {
        this.removeHeightObserver();
      }
    },
  },
  methods: {
    scrollToTop() {
      this.$refs.modalWrapper?.scrollTo({ top: 0 });
    },
    modalMouseDown() {
      this.isMousePressedWithinModal = true;
    },
    modalMouseUp() {
      this.isMousePressedWithinModal = false;
    },
    updateScroll() {
      if (this.isOpen) {
        this.disableScroll();
      } else {
        this.enableScroll();
      }
    },
    setZIndex() {
      const anotherModals = document.querySelectorAll('.ds-modal-wrapper--visible');
      if (anotherModals.length === 0) {
        return;
      }

      const zIndexes = [...anotherModals].map((modal) => {
        return parseInt(window.getComputedStyle(modal).getPropertyValue('z-index'), 10);
      });
      const maxZIndex = Math.max(...zIndexes);
      this.$refs.modalOverlay.style.zIndex = (maxZIndex + 1).toString();
      this.$refs.modalWrapper.style.zIndex = (maxZIndex + 2).toString();
    },
    disableScroll() {
      const scrollbarPresent = document.documentElement.scrollHeight > document.documentElement.clientHeight;
      if (!scrollbarPresent) {
        return;
      }
      if (document.documentElement.classList.contains(this.stopScrollClass)) {
        return;
      }
      this.scrollTop = document.documentElement.scrollTop;
      document.documentElement.style.top = `-${this.scrollTop}px`;
      document.documentElement.classList.add(this.stopScrollClass);
      this.hasLockedScroll = true;
    },
    // Only the modal that locked the page may unlock it. A modal that never locked it (one that
    // was never opened, or was stacked on an already open one) would otherwise unlock the page
    // under the modal still open and jump it to its own saved position of 0.
    enableScroll() {
      if (!this.hasLockedScroll) {
        return;
      }

      this.hasLockedScroll = false;
      document.documentElement.classList.remove(this.stopScrollClass);
      document.documentElement.style.removeProperty('top');
      document.documentElement.scrollTop = this.scrollTop;
    },
    closeOnBackgroundClick() {
      if (this.isMousePressedWithinModal) {
        this.isMousePressedWithinModal = false;
        return;
      }

      this.$emit('backgroundClick');

      if (this.closeOnOutsideClick) {
        this.$emit('close');
      }
    },
    closeModalOnEscKeyClick(event) {
      if (event.key === 'Escape') {
        this.$emit('close');
      }
    },
    stoppedPropagation() {
      this.$emit('stoppedPropagation');
    },
    checkTopAligned(modalHeight, windowHeight) {
      if (this.forceTopAligned) {
        this.topAligned = true;
        return;
      }

      if (modalHeight > windowHeight - 160) {
        this.topAligned = true;
      } else {
        this.topAligned = false;
      }
    },
    checkTopAlignedThrottled: throttle(function () {
      if (!this.$refs.modal) {
        return;
      }

      this.checkTopAligned(this.$refs.modal.offsetHeight, window.innerHeight);
    }, 500),
    initModalHeightObserver() {
      if (!window.ResizeObserver) {
        return;
      }
      this.resizeModalObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const modalHeight = entry?.borderBoxSize ? entry.borderBoxSize[0].blockSize : this.$refs.modal.offsetHeight;
          const windowHeight = window.innerHeight;
          this.checkTopAligned(modalHeight, windowHeight);
        });
      });
    },
    addHeightObserver() {
      window.addEventListener('resize', this.checkTopAlignedThrottled);
      if (this.resizeModalObserver) {
        this.resizeModalObserver.observe(this.$refs.modal);
      }
    },
    removeHeightObserver() {
      window.removeEventListener('resize', this.checkTopAlignedThrottled);
      if (this.resizeModalObserver) {
        this.resizeModalObserver.disconnect();
      }
    },
  },

  mounted() {
    this.initModalHeightObserver();

    if (this.isOpen) {
      this.updateScroll();
      this.setZIndex();
      this.addHeightObserver();
    }
    if (this.closeOnEscKeyClick) {
      document.addEventListener('keydown', this.closeModalOnEscKeyClick);
    }
  },

  beforeUnmount() {
    this.removeHeightObserver();
    this.enableScroll();

    if (this.closeOnEscKeyClick) {
      document.removeEventListener('keydown', this.closeModalOnEscKeyClick);
    }
  },
  unmounted() {
    this.isMousePressedWithinModal = false;
  },
};
</script>
